import type { SchoolBranding } from '@/types/composition-functions';
import { updatePrimaryPalette, palette } from '@primeuix/themes';
import { RESOLVE_SCHOOL } from '@/graphql/Queries/branding';

const branding = ref<SchoolBranding | null>(null);
const resolved = ref(false);

const DEFAULTS = {
  app_name: 'GradeVerse',
  primary_color: '#2B5740',
  accent_color: null,
  hide_gradeverse_branding: false,
};

const PLATFORM_HOSTS = new Set([
  'gradeverse.com',
  'www.gradeverse.com',
  'staging.gradeverse.com',
  'demo.gradeverse.com',
  'app.gradeverse.com',
  'localhost',
  '127.0.0.1',
]);

export function isTenantHost(): boolean {
  if (typeof window === 'undefined') return false;
  return !PLATFORM_HOSTS.has(window.location.hostname);
}

function applyPrimaryPalette(hex: string) {
  const customPalette = palette(hex);
  updatePrimaryPalette(customPalette);
}

export function useBranding() {
  const appName = computed(() =>
    branding.value?.app_name
    || branding.value?.school_name
    || DEFAULTS.app_name,
  );
  const primaryColor = computed(() => branding.value?.primary_color || DEFAULTS.primary_color);
  const accentColor = computed(() => branding.value?.accent_color || null);
  const isWhiteLabel = computed(() => branding.value?.hide_gradeverse_branding === true);
  const schoolLogo = computed(() => branding.value?.logo || null);
  const loginBgUrl = computed(() => branding.value?.login_bg_url || null);

  function detectSchoolIdentifier(): { slug?: string; domain?: string } {
    const hostname = window.location.hostname;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      const params = new URLSearchParams(window.location.search);
      const devSlug = params.get('school');
      if (devSlug) return { slug: devSlug };
      return {};
    }

    if (!hostname.endsWith('.gradeverse.com')) {
      return { domain: hostname };
    }

    const parts = hostname.split('.');
    if (parts.length >= 3 && parts[0] !== 'www' && parts[0] !== 'staging' && parts[0] !== 'app') {
      return { slug: parts[0] };
    }

    return {};
  }

  async function resolveAndApply(identifierOverride?: { slug?: string; domain?: string }) {
    // Pages that KNOW their school (e.g. a public form, via its slug) can resolve
    // branding explicitly — platform-domain URLs carry no school in the hostname.
    const identifier = identifierOverride ?? detectSchoolIdentifier();

    // Already resolved to a school; don't clobber it with a weaker signal.
    if (!identifierOverride && branding.value?.school_id) {
      resolved.value = true;
      return;
    }

    if (!identifier.slug && !identifier.domain) {
      resolved.value = true;
      return;
    }

    try {
      const { apolloClient } = await import('@/apolloClient');
      const { data } = await apolloClient.query({
        query: RESOLVE_SCHOOL,
        variables: identifier,
      });

      if (data?.resolveSchool) {
        branding.value = data.resolveSchool;
        applyBranding();

        // Host branding supplies the login default, not an override of an
        // authenticated user's explicit school choice (even if this resolves late).
        if (branding.value?.school_id &&
          (!localStorage.getItem('token') || !localStorage.getItem('current_school_id'))) {
          localStorage.setItem('current_school_id', branding.value.school_id);
        }
      }
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[useBranding] resolve failed:', err);
    } finally {
      resolved.value = true;
    }
  }

  function applyBranding() {
    if (!branding.value) return;

    if (branding.value.primary_color) {
      applyPrimaryPalette(branding.value.primary_color);
      // Match the browser/PWA/mobile chrome (address bar, Android task switcher)
      // to the tenant's brand instead of the GradeVerse green.
      const themeMeta = document.querySelector('meta[name=\'theme-color\']') as HTMLMetaElement | null;
      if (themeMeta) themeMeta.content = branding.value.primary_color;
    }

    if (branding.value.accent_color) {
      document.documentElement.style.setProperty('--brand-accent', branding.value.accent_color);
    }

    document.title = branding.value.app_name || branding.value.school_name || DEFAULTS.app_name;

    if (branding.value.favicon_url) {
      // Update the standard favicon AND the apple-touch-icon. The old
      // `link[rel~='icon']` selector never matched `apple-touch-icon`, so the
      // iOS "Add to Home Screen" icon stayed GradeVerse for white-label tenants.
      const iconLinks = document.querySelectorAll<HTMLLinkElement>(
        'link[rel~=\'icon\'], link[rel=\'apple-touch-icon\']',
      );
      iconLinks.forEach((link) => { link.href = branding.value!.favicon_url!; });
    }
  }

  function updateBranding(settings: Partial<SchoolBranding>) {
    branding.value = { ...branding.value, ...settings } as SchoolBranding;
    applyBranding();
  }

  return {
    branding,
    resolved,
    appName,
    primaryColor,
    accentColor,
    isWhiteLabel,
    schoolLogo,
    loginBgUrl,
    resolveAndApply,
    updateBranding,
  };
}
